import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaChartLine, 
  FaRobot, 
  FaCalculator, 
  FaBell, 
  FaDollarSign, 
  FaBullhorn,
  FaCheck,
  FaArrowRight,
  FaStar,
  FaTrophy,
  FaUsers,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';
import './Landing.css';

const Landing = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const testimonials = [
    {
      type: 'testimonial',
      name: 'Sarah Johnson',
      role: 'Amazon FBA Seller',
      image: '👩‍💼',
      rating: 5,
      text: 'Genia+ helped me recover $12,000 in lost FBA reimbursements in just 3 months. The AI insights are incredible!',
      stats: { revenue: '+45%', time: '10hrs/week saved' }
    },
    {
      type: 'testimonial',
      name: 'Michael Chen',
      role: 'E-commerce Entrepreneur',
      image: '👨‍💼',
      rating: 5,
      text: 'The profit tracker finally shows me my REAL profits. No more guessing. This tool paid for itself in the first week.',
      stats: { profit: '+32%', products: '250+ managed' }
    },
    {
      type: 'achievement',
      title: 'Trusted by Sellers Worldwide',
      stats: [
        { icon: <FaUsers />, value: '5,000+', label: 'Active Sellers' },
        { icon: <FaDollarSign />, value: '$2.5M+', label: 'Money Recovered' },
        { icon: <FaTrophy />, value: '4.9/5', label: 'Average Rating' }
      ]
    },
    {
      type: 'testimonial',
      name: 'Emily Rodriguez',
      role: 'Amazon Seller',
      image: '👩‍💻',
      rating: 5,
      text: 'The predictive alerts saved my business. I was notified about inventory issues before they became problems.',
      stats: { alerts: '50+ prevented', savings: '$8,500' }
    },
    {
      type: 'success',
      title: 'Real Results from Real Sellers',
      cases: [
        { metric: '156%', label: 'Average ROI Increase' },
        { metric: '23hrs', label: 'Time Saved per Month' },
        { metric: '94%', label: 'Customer Satisfaction' }
      ]
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const features = [
    {
      icon: <FaChartLine />,
      title: 'Profit Tracker',
      description: 'Track your real profits with detailed analytics and insights'
    },
    {
      icon: <FaRobot />,
      title: 'AI Assistant',
      description: 'Get intelligent recommendations powered by advanced AI'
    },
    {
      icon: <FaCalculator />,
      title: 'Smart Calculator',
      description: 'Calculate FBA fees, profits, and ROI instantly'
    },
    {
      icon: <FaBell />,
      title: 'Predictive Alerts',
      description: 'Get notified before problems affect your business'
    },
    {
      icon: <FaDollarSign />,
      title: 'Money Recovery',
      description: 'Identify and recover lost FBA reimbursements'
    },
    {
      icon: <FaBullhorn />,
      title: 'Amazon Ads',
      description: 'Optimize your advertising campaigns with AI insights'
    }
  ];

  const plans = [
    {
      name: 'Starter',
      price: 39,
      period: 'month',
      description: 'Perfect for new sellers',
      features: [
        'Up to 50 products',
        'Dashboard & Analytics',
        'Sync every 12 hours',
        'Order history (90 days)',
        'AI Calculator',
        'AI Chat (50 messages/month)',
        'Email support'
      ],
      cta: 'Start Free Trial',
      popular: false
    },
    {
      name: 'Growth',
      price: 79,
      period: 'month',
      description: 'For growing sellers',
      features: [
        'Up to 500 products',
        'Advanced Dashboard',
        'Sync every 3 hours',
        'Order history (1 year)',
        'AI Calculator',
        'AI Chat (500 messages/month)',
        'Profit Tracker',
        'Predictive Alerts',
        'Priority support'
      ],
      cta: 'Start Free Trial',
      popular: true
    },
    {
      name: 'Pro',
      price: 149,
      period: 'month',
      description: 'For serious Amazon sellers',
      features: [
        'Unlimited products',
        'Premium Dashboard',
        'Sync every hour',
        'Unlimited order history',
        'AI Calculator',
        'Unlimited AI Chat',
        'Profit Tracker',
        'Predictive Alerts',
        'Money Recovery',
        'Amazon Ads Management',
        'Dedicated support'
      ],
      cta: 'Start Free Trial',
      popular: false
    }
  ];

  return (
    <div className="landing-page">
      {/* Navbar */}
      <nav className="landing-navbar">
        <div className="container">
          <div className="navbar-content">
            <Link to="/" className="navbar-logo">
              <span className="logo-text">GENIA+</span>
            </Link>
            <div className="navbar-actions">
              <Link to="/login" className="btn btn-text">Sign In</Link>
              <Link to="/register" className="btn btn-primary">Start Free Trial</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">
              Grow Your Amazon Business with AI-Powered Insights
            </h1>
            <p className="hero-subtitle">
              Genia+ helps Amazon sellers maximize profits, recover lost money, 
              and make smarter decisions with advanced analytics and AI.
            </p>
            <div className="hero-cta">
              <Link to="/register" className="btn btn-primary btn-lg">
                Start Free Trial <FaArrowRight />
              </Link>
              <p className="hero-note">7-day free trial • No credit card required</p>
            </div>
          </div>
          <div className="hero-image">
            {/* Testimonials Carousel */}
            <div className="testimonials-carousel">
              <button className="carousel-btn prev" onClick={prevSlide}>
                <FaChevronLeft />
              </button>
              
              <div className="carousel-content">
                {testimonials[currentSlide].type === 'testimonial' && (
                  <div className="testimonial-slide">
                    <div className="testimonial-header">
                      <div className="testimonial-avatar">{testimonials[currentSlide].image}</div>
                      <div className="testimonial-info">
                        <h4>{testimonials[currentSlide].name}</h4>
                        <p>{testimonials[currentSlide].role}</p>
                        <div className="testimonial-rating">
                          {[...Array(testimonials[currentSlide].rating)].map((_, i) => (
                            <FaStar key={i} />
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="testimonial-text">"{testimonials[currentSlide].text}"</p>
                    <div className="testimonial-stats">
                      {Object.entries(testimonials[currentSlide].stats).map(([key, value]) => (
                        <div key={key} className="stat-item">
                          <span className="stat-value">{value}</span>
                          <span className="stat-label">{key}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {testimonials[currentSlide].type === 'achievement' && (
                  <div className="achievement-slide">
                    <h3>{testimonials[currentSlide].title}</h3>
                    <div className="achievement-stats">
                      {testimonials[currentSlide].stats.map((stat, idx) => (
                        <div key={idx} className="achievement-item">
                          <div className="achievement-icon">{stat.icon}</div>
                          <div className="achievement-value">{stat.value}</div>
                          <div className="achievement-label">{stat.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {testimonials[currentSlide].type === 'success' && (
                  <div className="success-slide">
                    <h3>{testimonials[currentSlide].title}</h3>
                    <div className="success-cases">
                      {testimonials[currentSlide].cases.map((item, idx) => (
                        <div key={idx} className="success-item">
                          <div className="success-metric">{item.metric}</div>
                          <div className="success-label">{item.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <button className="carousel-btn next" onClick={nextSlide}>
                <FaChevronRight />
              </button>

              <div className="carousel-indicators">
                {testimonials.map((_, idx) => (
                  <button
                    key={idx}
                    className={`indicator ${idx === currentSlide ? 'active' : ''}`}
                    onClick={() => setCurrentSlide(idx)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <div className="section-header">
            <h2>Everything You Need to Succeed on Amazon</h2>
            <p>Powerful features designed for Amazon FBA sellers</p>
          </div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section">
        <div className="container">
          <div className="section-header">
            <h2>Simple, Transparent Pricing</h2>
            <p>Choose the plan that fits your business</p>
          </div>
          <div className="pricing-grid">
            {plans.map((plan, index) => (
              <div 
                key={index} 
                className={`pricing-card ${plan.popular ? 'popular' : ''}`}
              >
                {plan.popular && <div className="popular-badge">Most Popular</div>}
                <div className="pricing-header">
                  <h3>{plan.name}</h3>
                  <div className="pricing-price">
                    <span className="currency">$</span>
                    <span className="amount">{plan.price}</span>
                    <span className="period">/{plan.period}</span>
                  </div>
                  <p className="pricing-description">{plan.description}</p>
                </div>
                <ul className="pricing-features">
                  {plan.features.map((feature, idx) => (
                    <li key={idx}>
                      <FaCheck className="check-icon" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link 
                  to="/register" 
                  className={`btn ${plan.popular ? 'btn-primary' : 'btn-outline'} btn-block`}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Grow Your Amazon Business?</h2>
            <p>Join hundreds of sellers who trust Genia+ to maximize their profits</p>
            <Link to="/register" className="btn btn-primary btn-lg">
              Start Your Free Trial <FaArrowRight />
            </Link>
            <p className="cta-note">7-day free trial • No credit card required • Cancel anytime</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="footer-logo">
                <span className="footer-logo-text">GENIA+</span>
              </div>
              <p>AI-powered Amazon seller tools</p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Product</h4>
                <Link to="/features">Features</Link>
                <Link to="/pricing">Pricing</Link>
                <Link to="/register">Sign Up</Link>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <Link to="/about">About</Link>
                <Link to="/contact">Contact</Link>
              </div>
              <div className="footer-column">
                <h4>Legal</h4>
                <Link to="/terms">Terms of Service</Link>
                <Link to="/privacy">Privacy Policy</Link>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2026 Genia+. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
